import AuthForm from '@/components/features/auth/AuthForm'
import AuthPage from '@/components/features/auth/AuthPage'
import {
  CredentialController,
  TextController,
} from '@/components/forms/FormController'
import ErrorMessage from '@/components/utils/ErrorMessage'
import { useAuth } from '@/context/AuthContext'
import { zodResolver } from '@/lib/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@droidmap/frontend-ui'

/**
 * `?email=` pre-fills the email field (#3058): the organisation-invitation
 * email for an address with no account links here, and the invitation is
 * matched to whatever address they sign up with. Optional, and anything that
 * isn't a single well-formed address — an array, junk, an oversized value —
 * degrades to the blank form via `.catch(undefined)` rather than the router's
 * error boundary, since we don't control how a link from an email arrives.
 * Exported for the route spec.
 */
export const signUpSearchSchema = z.object({
  email: z.string().trim().max(254).email().optional().catch(undefined),
})

export const Route = createFileRoute('/_unauthenticated/signup')({
  validateSearch: signUpSearchSchema,
  component: SignUp,
})

const signUpSchema = z.object({
  email: z.string().email('Invalid email address'),
  username: z
    .string()
    .regex(
      /^[\p{L}\p{M}\p{S}\p{N}\p{P}]{1,128}$/u,
      'Username must be 1-128 characters and contain only letters, marks, symbols, numbers, or punctuation.',
    ),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  password: z.string().min(12, 'Password must be at least 12 characters'),
})

type SignUpFormValues = z.infer<typeof signUpSchema>

const initialValues: SignUpFormValues = {
  email: '',
  username: '',
  firstName: '',
  lastName: '',
  password: '',
}

function SignUp() {
  const { email: invitedEmail } = Route.useSearch()
  const navigate = useNavigate()
  const auth = useAuth()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { ...initialValues, email: invitedEmail ?? '' },
    mode: 'onBlur',
  })

  const onSubmit = async (data: SignUpFormValues) => {
    setError('')
    setLoading(true)
    try {
      const { success, message, nextStep } = await auth.signUp(data)
      if (!success) {
        setError(message || 'An unknown error occurred')
        return
      }
      switch (nextStep) {
        case 'CONFIRM_SIGN_UP':
          navigate({
            to: '/confirm-signup',
            search: { username: data.username },
          })
          break
        case 'COMPLETE_AUTO_SIGN_IN': {
          const signInOutput = await auth.autoSignIn()
          if (signInOutput.isSignedIn) {
            navigate({ to: '/profile' })
          }
          break
        }
        case 'DONE':
          navigate({ to: '/profile' })
          break
        default:
          throw new Error('Unexpected sign up step')
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthPage title={'Sign up'}>
      <AuthForm onSubmit={handleSubmit(onSubmit)}>
        <CredentialController
          name="email"
          control={control}
          label="Email address"
          errors={errors}
          autoComplete="email"
        />
        <CredentialController
          name="username"
          control={control}
          label="Username"
          errors={errors}
          autoComplete="username"
        />
        {/* Identity, not credentials: these stay on the ignored `Input`.
            Losing identity fill on a once-per-account form beats putting a
            fill target next to the password field. (#2848) */}
        <TextController
          name="firstName"
          control={control}
          label="First name"
          errors={errors}
        />
        <TextController
          name="lastName"
          control={control}
          label="Last name"
          errors={errors}
        />
        <CredentialController
          name="password"
          control={control}
          label="Password (at least 12 characters)"
          errors={errors}
          type="password"
          autoComplete="new-password"
        />
        {error && <ErrorMessage content={error} />}
        <Button
          type="submit"
          className="w-full"
          isLoading={loading}
          disabled={loading}
        >
          Sign up
        </Button>
      </AuthForm>
      <p className="text-text mt-10 text-center text-sm">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => navigate({ to: '/login' })}
          className="font-semibold leading-6 text-accent hover:text-accent-hi"
        >
          Login here
        </button>
      </p>
    </AuthPage>
  )
}
